import React, { Component } from "react";
import Totals from "../components/Totals";
import QuoteName from "../components/QuoteName";
import QuoteFinance from "../components/QuoteFinance";
import QuoteBillingInformation from "../components/QuoteBillingInformation";
import QuoteHardwareItems from "../components/QuoteHardwareItems";
import QuoteSoftwareItems from "../components/QuoteSoftwareItems";
import QuoteSaasItems from "../components/saas/QuoteSaasItems"
import Alerts from "../components/shared/Alerts";
import EmptyQuote from "../components/EmptyQuote";
import { 
  setItemValidationMap, 
  setTonerRecycleBox, 
  refreshSaasTargetTotal,
  setSoftwareDeliveryAddressId,
  setSoftwareInstallationAddressId,
  setSaasDeliveryAddressId,
  setSaasInstallationAddressId,
  setSameSoftwareInstallDeliveryAddress,
  setSameSaasInstallDeliveryAddress,
  getQuoteBackendValidationMessages
} from "../redux/actions";
import {
  topLevelHardwareItems,
  topLevelSoftwareItems,
  topLevelSaasItems,
  validationMessages,
  quoteForBackend,
  hasSoftware,
  hasSaas
} from "../redux/selectors";
import Popup from "../components/Popup";
import { connect } from "react-redux";
import style from "./Quote.module.css";
import { submitQuote, validateQuoteItems } from "../api";
import { Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { UNIFLOW_SOFTWARE_PATH } from "../constants";
import SoftwareAddresses from "../components/SoftwareAddresses";
import TonerRecycle from "../components/TonerRecycle";
import ServiceTerm from "../components/ServiceTerm"
import {IS_NZ} from "../constants"

class Quote extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      errorMessage: ""
    };
  }

  async componentDidMount() {
    // Refresh the quote item validation
    const validationMap = await validateQuoteItems(
      this.props.quoteForBackend.items,
      this.props.customerGroupId
    );

    this.props.setItemValidationMap(validationMap);
    this.props.refreshSaasTargetTotal()
    this.props.getQuoteBackendValidationMessages();
  }

  get canSaveAsDraft() {
    return !this.state.isLoading && this.props.quoteName;
  }

  get canSaveForApproval() {
    return (
      !this.state.isLoading &&
      (this.props.isFinanceConfigured || this.props.financeType === "CASH") &&
      this.props.validationMessages.length === 0
    );
  }

  handleSubmitBtnClick = async (e, status) => {
    e.preventDefault();

    try {
      // No double submitting during processing
      this.setState({ isLoading: true });

      await submitQuote(
        { ...this.props.quoteForBackend, status },
        this.props.opportunity
      );

      // One can not submit again once it is successfully submitted,
      // so we do not set "isLoading" here.
      window.location = this.props.returnUrl;
    } catch (e) {
      this.setState({
        isLoading: false, // Allow retrying
        errorMessage: e.message || e.toString()
      });
    }
  };

  get hasSaas() {
    return this.props.quoteItems.filter(item => item.isSaas).length > 0
  }

  render() {
    return this.props.quoteItems.length === 0 ? (
      <EmptyQuote />
    ) : (
      <Form className={this.hasSaas ? style.saasRoot : style.root}>
        <Alerts
          alerts={this.props.validationMessages}
          backendalerts={this.props.backendValidationMessages}
          className={style.alerts}
        />

        <h2>Quote Name</h2>
        <QuoteName />

        {this.props.topLevelHardwareItems.length ? <h2>Hardware</h2> : null}
        <QuoteHardwareItems hardwareItems={this.props.topLevelHardwareItems} />

        {this.props.topLevelSoftwareItems.length ? (
          <h2>
            Software
            <Button
              as={Link}
              to={UNIFLOW_SOFTWARE_PATH}
              size="sm"
              variant="secondary"
              className="float-right"
            >
              Add Software Items
            </Button>
          </h2>
        ) : null}
        <QuoteSoftwareItems softwareItems={this.props.topLevelSoftwareItems} />

        {this.props.topLevelSaasItems.length ? <h2>Software-as-a-Service</h2> : null}
        <QuoteSaasItems saasItems={this.props.topLevelSaasItems} />

        {this.props.hasSoftware ? (
          <>
            <h2>Addresses for Software</h2>
            <SoftwareAddresses 
              DeliveryAddressId={this.props.softwareDeliveryAddressId}
              InstallationAddressId={this.props.softwareInstallationAddressId}
              setDeliveryAddressId={this.props.setSoftwareDeliveryAddressId}
              setInstallationAddressId={this.props.setSoftwareInstallationAddressId}
              sameAddress={this.props.sameSoftwareInstallDeliveryAddress}
              setSameAddress={this.props.setSameSoftwareInstallDeliveryAddress}
              type="Software"
            />
          </>
        ) : null}

        {this.props.hasSaas ? (
          <>
            <h2>Addresses for Software-as-a-Service</h2>
            <SoftwareAddresses 
              DeliveryAddressId={this.props.saasDeliveryAddressId}
              InstallationAddressId={this.props.saasInstallationAddressId}
              setDeliveryAddressId={this.props.setSaasDeliveryAddressId}
              setInstallationAddressId={this.props.setSaasInstallationAddressId}
              sameAddress={this.props.sameSaasInstallDeliveryAddress}
              setSameAddress={this.props.setSameSaasInstallDeliveryAddress}
              type="SaaS"
            />
          </>
        ) : null}

        <h2>Service Term</h2>
        <ServiceTerm />  

        {IS_NZ ? (
          <>
            <h2>Recycling</h2>
            <TonerRecycle />  
          </>
        ) : null}
        

        <h2>Finance</h2>
        <QuoteFinance />

        <h2>Billing Information</h2>
        <QuoteBillingInformation opportunity={this.props.opportunity} />

        <Totals />

        <div className={style.buttons}>
          <Button
            variant="secondary"
            size="lg"
            onClick={e => this.handleSubmitBtnClick(e, "PENDING")}
            disabled={!this.canSaveAsDraft}
          >
            Save as Draft
          </Button>

          <Button
            size="lg"
            onClick={e => this.handleSubmitBtnClick(e, "PENDING_APPROVAL")}
            disabled={!this.canSaveForApproval}
          >
            Submit Quote for Approval
          </Button>
        </div>

        <Popup
          show={!!this.state.errorMessage}
          title="Quote Submission Failed"
          body={this.state.errorMessage.split("\n").map((message, index) => (
            <p key={index}>{message}</p>
          ))}
          onHide={() => this.setState({ errorMessage: "" })}
        />
      </Form>
    );
  }
}

export default connect(
  state => ({
    validationMessages: validationMessages(state),
    backendValidationMessages: state.quote.backendValidationMessages,
    quoteForBackend: quoteForBackend(state),
    customerGroupId: state.quote.customerGroupId,
    returnUrl: state.returnUrl,
    opportunity: state.opportunity,
    quoteName: state.quote.name,
    quoteItems: state.quote.items,
    topLevelHardwareItems: topLevelHardwareItems(state),
    topLevelSoftwareItems: topLevelSoftwareItems(state),
    topLevelSaasItems: topLevelSaasItems(state),
    hasSoftware: hasSoftware(state),
    hasSaas: hasSaas(state),
    isFinanceConfigured: state.quote.finance.isConfigured,
    financeType: state.quote.finance.financeType,
    tonerRecycleBox: state.quote.tonerRecycleBox,
    softwareDeliveryAddressId: state.quote.softwareDeliveryAddressId,
    softwareInstallationAddressId: state.quote.softwareInstallationAddressId,
    saasDeliveryAddressId: state.quote.saasDeliveryAddressId,
    saasInstallationAddressId: state.quote.saasInstallationAddressId,
    sameSoftwareInstallDeliveryAddress: state.quote.sameSoftwareInstallDeliveryAddress,
    sameSaasInstallDeliveryAddress: state.quote.sameSaasInstallDeliveryAddress,
  }),
  { setItemValidationMap,
  setTonerRecycleBox,
  refreshSaasTargetTotal,
  setSoftwareDeliveryAddressId,
  setSoftwareInstallationAddressId,
  setSaasDeliveryAddressId,
  setSaasInstallationAddressId,
  setSameSoftwareInstallDeliveryAddress,
  setSameSaasInstallDeliveryAddress,
  getQuoteBackendValidationMessages }
)(Quote);
